import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Star, 
  Triangle, 
  Circle, 
  Orbit, 
  Layers, 
  Zap, 
  Route, 
  Users 
} from "lucide-react";

export const PackSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const assistants = [
    {
      name: "NOVA",
      role: "Contenu",
      description: "Posts, carrousels, newsletters : hooks SEO / émotion / cash + micro-pratique.",
      icon: Star,
      color: "emerald",
    },
    {
      name: "PRISME",
      role: "Page (conversion éthique)",
      description: "Structure claire, \"ce que ce n'est pas\", objections, FAQ, CTA sans pression.",
      icon: Triangle,
      color: "amber",
    },
    {
      name: "PORTAIL",
      role: "Stories (tunnel IG)",
      description: "Séquences 8–12 stories : tension → preuve → objections → CTA DM / lien.",
      icon: Circle,
      color: "purple",
    },
    {
      name: "ORBITE",
      role: "Ads (si tu veux scaler)",
      description: "Angles, hooks, textes courts/longs, déclinaisons sceptique/symbolique/cash.",
      icon: Orbit,
      color: "blue",
    },
    {
      name: "NOYAU",
      role: "Protocoles entre séances",
      description: "Régulation + journaling + micro-action + option symbolique (sans imposer).",
      icon: Layers,
      color: "teal",
    },
    {
      name: "FRACTURE",
      role: "Coach anti-excuses (cash)",
      description: "Il te renvoie là où tu te mens. Décision + action 24h.",
      icon: Zap,
      color: "orange",
    },
    {
      name: "TRAJECTOIRE",
      role: "Plan 14 jours + accountability",
      description: "3 actions / jour max : visibilité + impact + intégration. Plan B fatigue.",
      icon: Route,
      color: "indigo",
    },
    {
      name: "DOUBLE",
      role: "\"Gilles H24\"",
      description: "Doux, profond, lucide : accueil → vérité → décision → journaling → action.",
      icon: Users,
      color: "rose",
    },
  ];

  const colorClasses = {
    emerald: "from-emerald-50 to-emerald-100/50 border-emerald-200 text-emerald-600",
    amber: "from-amber-50 to-amber-100/50 border-amber-200 text-amber-600",
    purple: "from-purple-50 to-purple-100/50 border-purple-200 text-purple-600",
    blue: "from-blue-50 to-blue-100/50 border-blue-200 text-blue-600",
    teal: "from-teal-50 to-teal-100/50 border-teal-200 text-teal-600",
    orange: "from-orange-50 to-orange-100/50 border-orange-200 text-orange-600",
    indigo: "from-indigo-50 to-indigo-100/50 border-indigo-200 text-indigo-600",
    rose: "from-rose-50 to-rose-100/50 border-rose-200 text-rose-600",
  };

  return (
    <section 
      ref={ref}
      className="section-padding bg-gradient-to-b from-white via-mint-whisper to-white relative overflow-hidden"
      data-testid="pack-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-emerald-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
      </div>

      <div className="lumen-container relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Le pack complet
            </span>
            <h2 className="section-title font-cormorant text-text-main mb-4">
              Ce que tu reçois dans <span className="text-emerald-700">LUMEN</span>
            </h2>
            <p className="font-manrope text-text-muted max-w-xl mx-auto">
              Un système d'assistants (prompts prêts à copier-coller) + des commandes rapides.
            </p>
          </motion.div>

          {/* Assistants grid - Bento style */}
          <div className="bento-grid">
            {assistants.map((assistant, index) => (
              <motion.div
                key={assistant.name}
                className={`assistant-card bg-gradient-to-br ${colorClasses[assistant.color]} transition-all duration-300`}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
              >
                {/* Icon */}
                <div className={`icon-circle mb-4 bg-white/80 border-current/20`}>
                  <assistant.icon className={`w-5 h-5 ${colorClasses[assistant.color].split(' ').pop()}`} />
                </div>

                {/* Name badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-cinzel text-lg tracking-wider font-semibold text-text-main">
                    {assistant.name}
                  </span>
                </div>

                {/* Role */}
                <p className="font-manrope text-sm font-medium text-text-main mb-3">
                  {assistant.role}
                </p>

                {/* Description */}
                <p className="font-manrope text-sm text-text-muted leading-relaxed">
                  {assistant.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
